import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';
import Modal from '../../components/Modal';
import CompanyService from '../../components/CompanyService';

const emptyForm = {
  name: '',
  data: {
    ice: '',
    if: '',
    patente: '',
    adresse: '',
    capital: '',
    cnss: '',
  },
  logo: null,
};

const companyFields = [
  { key: 'ice', label: 'ICE' },
  { key: 'if', label: 'IF' },
  { key: 'patente', label: 'Patente' },
  { key: 'adresse', label: 'Adresse' },
  { key: 'capital', label: 'Capital' },
  { key: 'cnss', label: 'CNSS' },
];

const extractCompany = (response) => response?.response || response?.data || response;

function CompaniesView() {
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resolveLogoUrl = useMemo(
    () => (logo) => {
      if (!logo) return '';
      if (/^https?:\/\//i.test(logo) || /^data:/i.test(logo)) return logo;
      return `${process.env.REACT_APP_PEGASUS_API_URL || ''}${logo}`;
    },
    []
  );

  const loadCompanies = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await CompanyService.fetchCompanies();
      const list = response?.response || response || [];
      setCompanies(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de charger les sociétés.');
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setLogoPreview('');
    setError('');
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value,
      },
    }));
  };

  const handleEdit = async (company) => {
    const id = company?._id || company?.id;
    if (!id) return;

    setError('');

    try {
      const response = await CompanyService.fetchCompany(id);
      const fullCompany = extractCompany(response) || company;
      setEditingId(id);
      setFormData({
        name: fullCompany?.name || '',
        data: {
          ice: fullCompany?.data?.ice || '',
          if: fullCompany?.data?.if || '',
          patente: fullCompany?.data?.patente || '',
          adresse: fullCompany?.data?.adresse || '',
          capital: fullCompany?.data?.capital || '',
          cnss: fullCompany?.data?.cnss || '',
        },
        logo: null,
      });
      setLogoPreview(resolveLogoUrl(fullCompany?.logo));
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de charger la société.');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, logo: file }));
    setLogoPreview(file ? URL.createObjectURL(file) : '');
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        name: formData.name.trim(),
        data: {
          ice: formData.data.ice.trim(),
          if: formData.data.if.trim(),
          patente: formData.data.patente.trim(),
          adresse: formData.data.adresse.trim(),
          capital: formData.data.capital.trim(),
          cnss: formData.data.cnss.trim(),
        },
        logo: formData.logo,
      };

      if (editingId) {
        await CompanyService.updateCompany(editingId, payload);
      } else {
        await CompanyService.createCompany(payload);
      }

      resetForm();
      await loadCompanies();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette société ?')) return;

    setError('');
    try {
      await CompanyService.deleteCompany(id);
      await loadCompanies();
      if (editingId === id) {
        resetForm();
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Sociétés
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestion des sociétés réservée aux administrateurs.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadCompanies}
            disabled={isLoading}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            data-bs-toggle="modal"
            data-bs-target="#companyModal"
            onClick={resetForm}
          >
            Ajouter une société
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Logo</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>ICE</TableCell>
                <TableCell>IF</TableCell>
                <TableCell>Patente</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>Capital</TableCell>
                <TableCell>CNSS</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Aucune société trouvée.
                  </TableCell>
                </TableRow>
              ) : null}

              {companies.map((company) => {
                const companyId = company?._id || company?.id;

                return (
                  <TableRow key={companyId} hover>
                    <TableCell>
                      <Avatar
                        src={resolveLogoUrl(company?.logo)}
                        alt={company?.name || 'Company'}
                        variant="rounded"
                        sx={{ width: 48, height: 48 }}
                      >
                        <BusinessIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell>{company?.name || '-'}</TableCell>
                    <TableCell>{company?.data?.ice || '-'}</TableCell>
                    <TableCell>{company?.data?.if || '-'}</TableCell>
                    <TableCell>{company?.data?.patente || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{company?.data?.adresse || '-'}</TableCell>
                    <TableCell>{company?.data?.capital || '-'}</TableCell>
                    <TableCell>{company?.data?.cnss || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        size="small"
                        data-bs-toggle="modal"
                        data-bs-target="#companyModal"
                        onClick={() => handleEdit(company)}
                        aria-label="Modifier la société"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(companyId)}
                        aria-label="Supprimer la société"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        modalId="companyModal"
        modalType="modal-xl"
        onSubmit={handleSubmit}
        submitLabel={isSubmitting ? 'Enregistrement...' : editingId ? 'Mettre à jour' : 'Créer'}
        submitDisabled={isSubmitting || !formData.name.trim()}
        title={editingId ? 'Modifier la société' : 'Nouvelle société'}
        onClose={resetForm}
      >
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Nom"
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            fullWidth
            required
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {companyFields.map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                value={formData.data[field.key]}
                onChange={(event) => handleFieldChange(field.key, event.target.value)}
                fullWidth
                multiline={field.key === 'adresse'}
                minRows={field.key === 'adresse' ? 3 : undefined}
              />
            ))}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Logo
            </Typography>
            <input type="file" accept="image/*" onChange={handleFileChange} className="form-control" />
            {logoPreview ? (
              <Box sx={{ mt: 2 }}>
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  style={{ maxHeight: 120, borderRadius: 8, border: '1px solid #e0e0e0' }}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default CompaniesView;
